// src/data/reports.js
//
// SECTION 533 (2026-09-30, Bob: "What about reports from the platform about claims, discipline ..."). The officer's
// reports, READ-ONLY and DERIVED: every number is counted from the claims and discipline cases the viewer can
// already read (claimQueries.js and the disciplineCases query - the rules decide what that is). Nothing is stored,
// nothing is written. A Local Chairperson's reports are his territory; a General Chairperson's are his committee.
//
//   1. Claims status   - where every filed claim stands on the ladder, by office, and how long it has been open.
//   2. Deadlines       - the claim filing windows and carrier answers (unionCalendar.deadlinesFromClaims), the
//                        discipline hearing dates and investigation deadlines; overdue first.
//   3. Money           - claimTally's numbers (ruling 161: recovered money counts only with a document behind it).
//   4. Rule trends     - which rules are claimed most, and the last twelve months.
//   5. Discipline      - cases by stage, hearings coming, preservation demands not yet issued.
//   6. Activity        - claims filed and cases opened per month, per office.
// Drafts are the author's alone (ruling 56) and appear in no report.
import { LADDER } from './claimLadder';
import { tallyClaims, claimBucket } from './claimTally';
import { deadlinesFromClaims } from './unionCalendar';
import { currentSchedule, preservationStatus } from './hearingEvents';
import { disciplineDeadlines } from './claimClocks';   // section 539: one reading of the discipline dates

export const REPORTS = [
  { key: 'status', label: 'Claims status' },
  { key: 'deadlines', label: 'Deadlines' },
  { key: 'money', label: 'Money' },
  { key: 'rules', label: 'Rule trends' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'activity', label: 'Activity' },
];
export const NO_OFFICE = '(no office on the record)';
export const AGE_BANDS = [
  { key: '0-30', max: 30 }, { key: '31-60', max: 60 }, { key: '61-90', max: 90 }, { key: '91-180', max: 180 }, { key: 'over 180', max: Infinity },
];
export const DISCIPLINE_STAGES = [
  { key: 'notice_received', label: 'Notice Received' },
  { key: 'investigation_ordered', label: 'Investigation Ordered' },
  { key: 'investigation_complete', label: 'Investigation Complete' },
  { key: 'appeal_filed', label: 'Appeal Filed' },
  { key: 'decision_pending', label: 'Decision Pending' },
  { key: 'gc_review', label: 'GC Review' },
  { key: 'resolved', label: 'Resolved' },
];

const isDate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
export function today() { return new Date().toISOString().slice(0, 10); }
export function dayDiff(from, to) { return Math.round((Date.parse(to + 'T00:00:00Z') - Date.parse(from + 'T00:00:00Z')) / 86400000); }

// When a record entered the system, as YYYY-MM-DD: a Firestore Timestamp, {seconds}, a Date, or an ISO string.
export function dateOf(v) {
  if (!v) return null;
  if (typeof v === 'string') return /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null;
  if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d) ? null : d.toISOString().slice(0, 10); }
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  if (v instanceof Date) return isNaN(v) ? null : v.toISOString().slice(0, 10);
  return null;
}
export function claimFiledOn(c) { return (c && (isDate(c.presentedOn) ? c.presentedOn : (dateOf(c.filedAt) || dateOf(c.createdAt)))) || null; }
export function caseOpenedOn(k) { return (k && (isDate(k.noticeDate) ? k.noticeDate : dateOf(k.openedAt))) || null; }

export function filed(claims) { return (Array.isArray(claims) ? claims : []).filter(c => c && c.status && c.status !== 'draft'); }
export function liveCases(cases) { return (Array.isArray(cases) ? cases : []).filter(k => k && k.status && k.status !== 'draft'); }
const officeOf = (r) => String((r && r.scopeKey) || '') || NO_OFFICE;
const labelOf = (label, k) => (k === NO_OFFICE ? NO_OFFICE : ((typeof label === 'function' && label(k)) || k));

// 1. CLAIMS STATUS
export function statusReport(claims, opts) {
  const o = opts || {};
  const t = o.today || today();
  const list = filed(claims);
  const stages = LADDER.filter(s => s.key !== 'draft').map(s => ({ key: s.key, label: s.label, n: 0 }));
  const other = {};
  const offices = new Map();
  const ages = AGE_BANDS.map(b => ({ key: b.key, n: 0 }));
  let undated = 0, open = 0;
  const oldest = [];
  for (const c of list) {
    const st = stages.find(s => s.key === c.status);
    if (st) st.n++; else other[c.status] = (other[c.status] || 0) + 1;
    const k = officeOf(c);
    if (!offices.has(k)) offices.set(k, { office: k, label: labelOf(o.seatLabel, k), n: 0, open: 0, byStage: {} });
    const off = offices.get(k); off.n++; off.byStage[c.status] = (off.byStage[c.status] || 0) + 1;
    const b = claimBucket(c);
    if (b === 'open' || b === 'board') {
      open++; off.open++;
      const on = claimFiledOn(c);
      if (!on) { undated++; continue; }
      const age = Math.max(0, dayDiff(on, t));
      ages[AGE_BANDS.findIndex(x => age <= x.max)].n++;
      oldest.push({ id: c.docId || c.id, label: c.claimNumber ? 'Claim ' + c.claimNumber : (c.docId || c.id), claimant: c.claimantName || '', status: c.status, office: labelOf(o.seatLabel, k), filedOn: on, age });
    }
  }
  oldest.sort((a, b) => b.age - a.age);
  return {
    total: list.length, open, stages, other: Object.keys(other).map(k => ({ key: k, n: other[k] })),
    offices: Array.from(offices.values()).sort((a, b) => (a.office === NO_OFFICE) - (b.office === NO_OFFICE) || a.label.localeCompare(b.label)),
    ages, undated, oldest: oldest.slice(0, o.oldestN || 15),
    reconciles: stages.reduce((s, x) => s + x.n, 0) + Object.values(other).reduce((s, x) => s + x, 0) === list.length,
  };
}

// 2. DEADLINES - claims and discipline, one list, overdue first then soonest.
export function deadlineReport(claims, cases, opts) {
  const o = opts || {};
  const t = o.today || today();
  const horizon = Number.isFinite(o.horizonDays) ? o.horizonDays : 60;
  const rows = [];
  for (const d of deadlinesFromClaims(Array.isArray(claims) ? claims : [])) rows.push({ source: 'Claim', date: d.date, title: d.title, ref: d.claimId || null });
  for (const d of disciplineDeadlines(cases)) rows.push({ source: 'Discipline', date: d.date, title: d.title, ref: d.caseId });
  const out = rows.map(r => ({ ...r, days: dayDiff(t, r.date) })).filter(r => r.days <= horizon);
  out.sort((a, b) => a.date.localeCompare(b.date) || a.title.localeCompare(b.title));
  return { rows: out, overdue: out.filter(r => r.days < 0).length, week: out.filter(r => r.days >= 0 && r.days <= 7).length, horizon };
}

// 3. MONEY - claimTally, overall and per office.
export function moneyReport(claims, opts) {
  const o = opts || {};
  const list = filed(claims);
  const byOffice = new Map();
  for (const c of list) { const k = officeOf(c); if (!byOffice.has(k)) byOffice.set(k, []); byOffice.get(k).push(c); }
  return {
    total: tallyClaims(list),
    offices: Array.from(byOffice.entries()).map(([k, cs]) => ({ office: k, label: labelOf(o.seatLabel, k), tally: tallyClaims(cs) }))
      .sort((a, b) => (a.office === NO_OFFICE) - (b.office === NO_OFFICE) || a.label.localeCompare(b.label)),
  };
}

// 4. RULE TRENDS - the rule(s) a claim rests on, as the claim names them (claimPayload.claimBasisLabel's reading).
export function rulesOf(c) {
  if (!c) return [];
  if (Array.isArray(c.matchedRules) && c.matchedRules.length) return Array.from(new Set(c.matchedRules.map(r => r && String(r.rule || '').trim()).filter(Boolean)));
  const s = String(c.rulesViolated || c.violationType || c.claimType || '').trim();
  return s ? Array.from(new Set(s.split(/\s*;\s*/).filter(Boolean))) : [];
}
export function monthsBack(t, n) {
  const [y, m] = t.split('-').map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i--) { const d = new Date(Date.UTC(y, m - 1 - i, 1)); out.push(d.toISOString().slice(0, 7)); }
  return out;
}
export function ruleReport(claims, opts) {
  const o = opts || {};
  const t = o.today || today();
  const months = monthsBack(t, 12);
  const counts = new Map();
  let none = 0;
  for (const c of filed(claims)) {
    const rs = rulesOf(c);
    if (!rs.length) { none++; continue; }
    const mo = (claimFiledOn(c) || '').slice(0, 7);
    for (const r of rs) {
      if (!counts.has(r)) counts.set(r, { rule: r, n: 0, won: 0, lost: 0, open: 0, byMonth: {} });
      const x = counts.get(r); x.n++;
      const b = claimBucket(c);
      if (b === 'awarded') x.won++; else if (b === 'unsuccessful') x.lost++; else x.open++;
      if (months.includes(mo)) x.byMonth[mo] = (x.byMonth[mo] || 0) + 1;
    }
  }
  const rules = Array.from(counts.values()).sort((a, b) => b.n - a.n || a.rule.localeCompare(b.rule));
  return { rules: rules.slice(0, o.topN || 15), more: Math.max(0, rules.length - (o.topN || 15)), none, months };
}

// 5. DISCIPLINE
export function disciplineReport(cases, opts) {
  const o = opts || {};
  const t = o.today || today();
  const list = liveCases(cases);
  const stages = DISCIPLINE_STAGES.map(s => ({ ...s, n: 0 }));
  const other = {};
  const hearings = [];
  let preservationGaps = 0;
  const gaps = [];
  for (const k of list) {
    const st = stages.find(s => s.key === k.status);
    if (st) st.n++; else other[k.status] = (other[k.status] || 0) + 1;
    if (k.status === 'resolved') continue;
    const who = k.employeeName || k.memberName || k.caseNumber || k.id;
    const sch = currentSchedule(k.hearingEvents, k.hearingLog && k.hearingLog.hearingDate);
    if (sch.state === 'scheduled' && isDate(sch.date) && dayDiff(t, sch.date) >= 0) hearings.push({ id: k.id, who, date: sch.date, days: dayDiff(t, sch.date), office: labelOf(o.seatLabel, officeOf(k)) });
    const p = preservationStatus(k.hearingEvents, isDate(k.noticeDate) ? k.noticeDate : null);
    if (p.unissued > 0 && sch.state !== 'held') { preservationGaps++; gaps.push({ id: k.id, who, unissued: p.classes.filter(c => c.state === 'unissued').map(c => c.mediaClass) }); }
  }
  hearings.sort((a, b) => a.date.localeCompare(b.date));
  return {
    total: list.length, open: list.filter(k => k.status !== 'resolved').length,
    stages, other: Object.keys(other).map(k => ({ key: k, n: other[k] })),
    hearings, preservationGaps, gaps,
  };
}

// 6. ACTIVITY - claims filed and cases opened per month for the last twelve months, per office.
export function activityReport(claims, cases, opts) {
  const o = opts || {};
  const months = monthsBack(o.today || today(), 12);
  const row = () => months.reduce((m, k) => { m[k] = 0; return m; }, {});
  const claimsByMonth = row(), casesByMonth = row(), outcomesByMonth = row();
  const offices = new Map();
  for (const c of filed(claims)) {
    const mo = (claimFiledOn(c) || '').slice(0, 7);
    if (mo in claimsByMonth) {
      claimsByMonth[mo]++;
      const k = officeOf(c);
      if (!offices.has(k)) offices.set(k, { office: k, label: labelOf(o.seatLabel, k), byMonth: row(), n: 0 });
      offices.get(k).byMonth[mo]++; offices.get(k).n++;
    }
    const od = dateOf(c.outcomeRecordedAt) || (isDate(c.outcomeDate) ? c.outcomeDate : null);
    if (od && od.slice(0, 7) in outcomesByMonth && c.outcome) outcomesByMonth[od.slice(0, 7)]++;
  }
  for (const k of liveCases(cases)) { const mo = (caseOpenedOn(k) || '').slice(0, 7); if (mo in casesByMonth) casesByMonth[mo]++; }
  return { months, claimsByMonth, casesByMonth, outcomesByMonth,
    offices: Array.from(offices.values()).sort((a, b) => b.n - a.n || a.label.localeCompare(b.label)) };
}

// CSV for any table: rows of plain values; quotes doubled; formula-leading cells neutralised.
export function toCsv(header, rows) {
  const cell = (v) => {
    let s = v == null ? '' : String(v);
    if (/^[=+\-@]/.test(s)) s = "'" + s;
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  return [header].concat(rows).map(r => r.map(cell).join(',')).join('\n') + '\n';
}
